/* eslint-disable prefer-arrow/prefer-arrow-functions */
import hashIt from 'hash-it';
import { useEffect, useState } from 'react';

export enum RepetitionsEditState {
  None = 'None',
  Editing = 'Editing',
  Saving = 'Saving',
}

export interface Repetition {
  start: number;
  end: number;
}

interface UseEditRepetitionsProps {
  enabledRepetitions: Repetition[];
  disabledRepetitions: Repetition[];
  onSave: (disabled: Repetition[]) => Promise<void>;
}

export interface UseEditRepetitionsReturn {
  editState: RepetitionsEditState;
  startEditing: () => void;
  cancelEditing: () => void;
  saveChanges: () => Promise<void>;
  addToEnabled: (repetition: Repetition) => void;
  addToDisabled: (repetition: Repetition) => void;
  isEnabled: (repetition: Repetition) => boolean;
  isDisabled: (repetition: Repetition) => boolean;
  stateHash: number;
}

export function useEditRepetitions({
  enabledRepetitions,
  disabledRepetitions,
  onSave,
}: UseEditRepetitionsProps): UseEditRepetitionsReturn {
  const [editState, setEditState] = useState<RepetitionsEditState>(
    RepetitionsEditState.None
  );
  const [localEnabledRepetitions, setLocalEnabledRepetitions] = useState<
    Repetition[]
  >([]);
  const [localDisabledRepetitions, setLocalDisabledRepetitions] = useState<
    Repetition[]
  >([]);

  // Update local state when input props change and we're not in editing mode
  useEffect((): void => {
    if (editState !== RepetitionsEditState.Editing) {
      setLocalEnabledRepetitions(enabledRepetitions);
      setLocalDisabledRepetitions(disabledRepetitions);
    }
  }, [hashIt(enabledRepetitions), hashIt(disabledRepetitions), editState]);

  const startEditing = (): void => {
    setEditState(RepetitionsEditState.Editing);
    // Make a deep copy to avoid modifying the original arrays
    setLocalEnabledRepetitions([...enabledRepetitions]);
    setLocalDisabledRepetitions([...disabledRepetitions]);
  };

  const cancelEditing = (): void => {
    setEditState(RepetitionsEditState.None);
    // Reset to the original values
    setLocalEnabledRepetitions(enabledRepetitions);
    setLocalDisabledRepetitions(disabledRepetitions);
  };

  const saveChanges = async (): Promise<void> => {
    setEditState(RepetitionsEditState.Saving);
    try {
      await onSave(localDisabledRepetitions);
      setEditState(RepetitionsEditState.None);
    } catch (error) {
      // Handle error - you might want to set an error state or show a notification
      console.error('Failed to save repetitions:', error);
      setEditState(RepetitionsEditState.Editing);
    }
  };

  const addToEnabled = (repetition: Repetition): void => {
    if (editState !== RepetitionsEditState.Editing) return;

    // Remove from disabled if present
    setLocalDisabledRepetitions((prev: Repetition[]): Repetition[] =>
      prev.filter(
        (item: Repetition): boolean =>
          item.start !== repetition.start || item.end !== repetition.end
      )
    );

    // Add to enabled if not already present
    setLocalEnabledRepetitions((prev: Repetition[]): Repetition[] => {
      const exists = prev.some(
        (item: Repetition): boolean =>
          item.start === repetition.start && item.end === repetition.end
      );
      return exists ? prev : [...prev, repetition];
    });
  };

  const addToDisabled = (repetition: Repetition): void => {
    if (editState !== RepetitionsEditState.Editing) return;

    // Only remove from enabled if it's not the last one
    setLocalEnabledRepetitions((prev) => {
      // If there's only one enabled repetition, don't remove it
      if (prev.length <= 1) {
        return prev;
      }
      return prev.filter(
        (item) => item.start !== repetition.start || item.end !== repetition.end
      );
    });

    // Add to disabled if not already present
    setLocalDisabledRepetitions((prev) => {
      const exists = prev.some(
        (item) => item.start === repetition.start && item.end === repetition.end
      );
      return exists ? prev : [...prev, repetition];
    });
  };

  const isEnabled = (repetition: Repetition): boolean =>
    localEnabledRepetitions.some(
      (item) => item.start === repetition.start && item.end === repetition.end
    );

  const isDisabled = (repetition: Repetition): boolean =>
    localDisabledRepetitions.some(
      (item) => item.start === repetition.start && item.end === repetition.end
    );

  return {
    editState,
    startEditing,
    cancelEditing,
    saveChanges,
    addToEnabled,
    addToDisabled,
    isEnabled,
    isDisabled,
    stateHash: hashIt({
      enabledRepetitions: localEnabledRepetitions,
      disabledRepetitions: localDisabledRepetitions,
    }),
  };
}
